/*
 * Notify me when back in stock.
 *
 * ONE <notify-me-modal> per page (rendered from layout/theme.liquid via
 * snippets/notify-me-modal.liquid). ANY number of
 * [data-notify-me-trigger] buttons (snippets/notify-me-trigger.liquid).
 *
 * Triggers carry no JS of their own. A single delegated click listener on
 * document handles all of them, which means the feature survives, with no
 * re-initialisation, every re-render path in this theme:
 *   - collection facet/sort replacement (component-facets.js)
 *   - KROWN.productItemUpdateHelper card rebuilds (combined listings)
 *   - the product page's [data-update-block] innerHTML swap on variant change
 *     (which destroys any listener bound to an element inside the wrapper)
 *
 * CONTRACT WITH THE BACKEND - do not "improve" either of these:
 *   1. NO variantId / variant field of any kind is ever sent. Capture is
 *      product-level only; a variant field would create rows whose semantics
 *      are not implemented server-side.
 *   2. Every 2xx renders the SAME confirmation. The endpoint returns an
 *      identical generic 200 for new / duplicate / suppressed addresses so the
 *      endpoint cannot be probed to test whether an email is on the list.
 *      Adding an "already subscribed" state would reopen that hole.
 */

if ( typeof NotifyMeModal !== 'function' ) {

  class NotifyMeModal extends HTMLElement {

    constructor() {

      super();

      this.modal      = this.querySelector('modal-box');
      this.form       = this.querySelector('[data-notify-me-form]');
      this.firstName  = this.querySelector('[data-notify-me-first-name]');
      this.lastName   = this.querySelector('[data-notify-me-last-name]');
      this.email      = this.querySelector('[data-notify-me-email]');
      this.honeypot   = this.querySelector('[data-notify-me-honeypot]');
      this.optin      = this.querySelector('[data-notify-me-optin]');
      this.consent    = this.querySelector('[data-notify-me-consent-text]');
      this.titleEl    = this.querySelector('[data-notify-me-product-title]');
      this.imageEl    = this.querySelector('[data-notify-me-product-image]');
      this.submit     = this.querySelector('[data-notify-me-submit]');
      this.submitText = this.querySelector('[data-notify-me-submit-text]');
      this.done       = this.querySelector('[data-notify-me-success]');
      this.error      = this.querySelector('[data-notify-me-error]');
      this.errorText  = this.querySelector('[data-notify-me-error-text]');

      /* Name fields and the thumbnail are deliberately NOT in this guard — all
         three are optional, and a theme edit that removes one must not disable
         the whole modal. */
      if ( ! this.modal || ! this.form || ! this.email || ! this.submit ) return;

      /* component-modal.js toggles `${window.KT_PREFIX || ''}active`, not a bare
         'active'. Mirror it or the Tab cycle below silently never engages. */
      this.activeClass = (window.KT_PREFIX || '') + 'active';

      this.endpoint          = this.dataset.endpoint || '/apps/notify';
      this.locale            = this.dataset.locale || document.documentElement.lang || '';
      this.labelSubmit       = this.dataset.labelSubmit || this.submitText.textContent;
      this.labelWorking      = this.dataset.labelSubmitWorking || this.labelSubmit;
      this.errorInvalidEmail = this.dataset.errorInvalidEmail || 'Please enter a valid email address.';
      this.errorGeneric      = this.dataset.errorGeneric || 'Something went wrong. Please try again.';

      /* Merchant-controlled from Theme editor -> Settings -> "Back in stock".
         Anything other than the literal "false" means ticked, so a missing
         attribute (settings group lost to a theme update) keeps today's
         behaviour rather than silently flipping the opt-in off. */
      this.optinDefault = this.dataset.optinDefault !== 'false';

      this.product = { id: '', handle: '', title: '', image: '' };
      this.sending = false;

      document.addEventListener('click', this.onDocumentClick.bind(this));
      document.addEventListener('keydown', this.onDocumentKeydown.bind(this));

      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.email.addEventListener('input', this.clearStatus.bind(this));

    }

    /* ---------- opening ---------------------------------------------------- */

    onDocumentClick(e) {

      if ( ! e.target || typeof e.target.closest !== 'function' ) return;

      const trigger = e.target.closest('[data-notify-me-trigger]');
      if ( ! trigger ) return;

      e.preventDefault();
      this.open(trigger);

    }

    open(trigger) {

      this.product = {
        id: trigger.getAttribute('data-product-id') || '',
        handle: trigger.getAttribute('data-product-handle') || '',
        title: trigger.getAttribute('data-product-title') || '',
        image: trigger.getAttribute('data-product-image') || ''
      };

      if ( this.titleEl ) this.titleEl.textContent = this.product.title;

      /* A product with no featured image yields an empty attribute. Hide the
         <img> rather than letting it render a broken-image box, and clear src
         so the previous product's photo can never flash in this one's header. */
      if ( this.imageEl ) {
        if ( this.product.image ) {
          this.imageEl.src = this.product.image;
          this.imageEl.alt = this.product.title;
          this.imageEl.hidden = false;
        } else {
          this.imageEl.removeAttribute('src');
          this.imageEl.alt = '';
          this.imageEl.hidden = true;
        }
      }

      this.reset();

      /* ModalBox.hide() restores focus to window.lastFocusedElm, but nothing in
         the theme ever writes it. Without this the shopper's focus is dropped to
         <body> on close - on a 40-card grid that loses their place entirely. */
      window.lastFocusedElm = trigger;

      /* ModalBox captures window.inertElems in its constructor. This modal is
         rendered in layout/theme.liquid, so its constructor can run before
         section-header.js has stamped [data-js-inert] onto the #main children.
         Re-capture so "blockTabNavigation": true actually inerts the page. */
      window.inertElems = document.querySelectorAll('[data-js-inert]');

      if ( typeof this.modal.show === 'function' ) {
        this.modal.show();
      }

    }

    /* ---------- focus containment ------------------------------------------
       The theme's containment is the inert attribute on [data-js-inert], which
       section-header.js stamps only on #main > div. The header and footer
       sections render outside #main and stay tabbable, so we cycle Tab
       ourselves while the modal is active. */

    focusable() {

      const candidates = this.modal.querySelectorAll('a[href], button, input, select, textarea, [tabindex]');

      return Array.prototype.filter.call(candidates, function(elm) {
        return elm.tabIndex >= 0
          && ! elm.disabled
          && ( elm.offsetWidth > 0 || elm.offsetHeight > 0 );
      });

    }

    onDocumentKeydown(e) {

      if ( e.key !== 'Tab' ) return;
      if ( ! this.modal.classList.contains(this.activeClass) ) return;

      const items = this.focusable();
      if ( items.length === 0 ) return;

      const first = items[0];
      const last = items[items.length - 1];

      if ( e.shiftKey && document.activeElement === first ) {
        e.preventDefault();
        last.focus();
      } else if ( ! e.shiftKey && document.activeElement === last ) {
        e.preventDefault();
        first.focus();
      }

    }

    /* ---------- state ------------------------------------------------------ */

    reset() {

      this.sending = false;
      this.form.hidden = false;
      if ( this.done ) this.done.hidden = true;
      this.submit.disabled = false;
      this.submitText.textContent = this.labelSubmit;
      if ( this.firstName ) this.firstName.value = '';
      if ( this.lastName ) this.lastName.value = '';
      this.email.value = '';
      this.optin.checked = this.optinDefault;
      this.honeypot.value = '';
      this.clearStatus();

    }

    clearStatus() {

      if ( this.error ) this.error.hidden = true;
      if ( this.errorText ) this.errorText.textContent = '';
      this.email.removeAttribute('aria-invalid');

    }

    showError(message) {

      this.errorText.textContent = message;
      this.error.hidden = false;
      this.email.setAttribute('aria-invalid', 'true');

    }

    /* Replaces the form IN PLACE rather than closing the modal. A window that
       simply vanishes reads as "did that work?"; an explicit confirmation does
       not. The product header above stays visible, so the confirmation still
       names what it is confirming. */
    showSuccess() {

      this.clearStatus();
      this.form.hidden = true;

      if ( this.done ) {
        this.done.hidden = false;
        /* Hiding the form strands focus on a removed element. Move it to the
           confirmation, which is tabindex="-1" and aria-live, so the message is
           announced rather than focus falling to <body>. */
        this.done.focus();
      } else {
        this.modal.focus();
      }

    }

    restoreSubmit() {

      this.sending = false;
      this.submit.disabled = false;
      this.submitText.textContent = this.labelSubmit;

    }

    /* ---------- submit ----------------------------------------------------- */

    onSubmit(e) {

      e.preventDefault();

      if ( this.sending ) return;

      this.clearStatus();

      const email = this.email.value.trim();
      this.email.value = email;

      if ( ! this.email.checkValidity() ) {
        this.showError(this.errorInvalidEmail);
        this.email.focus();
        return;
      }

      /* Names are optional. Trim and cap them so a pasted essay cannot bloat the
         request; the server caps independently, this is just politeness. */
      const firstName = this.firstName ? this.firstName.value.trim().slice(0, 100) : '';
      const lastName  = this.lastName  ? this.lastName.value.trim().slice(0, 100)  : '';

      /* Double-submit guard: the App Proxy round trip is slow enough that an
         impatient shopper taps Submit twice. */
      this.sending = true;
      this.submit.disabled = true;
      this.submitText.textContent = this.labelWorking;

      const payload = {
        email: email,
        firstName: firstName,
        lastName: lastName,
        productId: String(this.product.id),
        productHandle: this.product.handle,
        productTitle: this.product.title,
        marketingOptIn: this.optin.checked === true,
        consentText: ( this.consent ? this.consent.textContent : '' ).replace(/\s+/g, ' ').trim(),
        consentUrl: window.location.href,
        locale: this.locale,
        website: this.honeypot.value
      };
      /* DO NOT ADD a variantId / variant / id key to payload. Product-level only. */

      const self = this;

      fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      }).then(function(response) {

        if ( response.ok ) {
          /* Identical confirmation for every 2xx - new, duplicate, suppressed. */
          self.showSuccess();
          return;
        }

        self.restoreSubmit();

        if ( response.status === 400 ) {
          self.showError(self.errorInvalidEmail);
          self.email.focus();
        } else {
          /* 401 (proxy signature) is never surfaced as itself. */
          self.showError(self.errorGeneric);
        }

      }).catch(function() {

        self.restoreSubmit();
        self.showError(self.errorGeneric);

      });

    }

  }

  if ( typeof customElements.get('notify-me-modal') == 'undefined' ) {
    customElements.define('notify-me-modal', NotifyMeModal);
  }

}
